<script setup>
import Layout from "../../../layouts/main.vue";
import mushroom from "cem-primary-api";
import { onMounted, ref, computed, watch, nextTick } from "vue";
import MethodService from "../../../service/MethodService";
import { Filter, Project, Sort } from "mushroomjs";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import StepDateTimePicker from "../../../components/StepDateTimePicker.vue";

const { locale, t } = useI18n();
const route = useRoute();
const router = useRouter();
const probeOptions = ref([]);
const strategyOptions = ref([]);
const selectedProbe = ref(null);
const selectedStrategy = ref(null);
const selectedSupportType = ref("tong_hop");
const dateRange = ref(null);
const datetimeRange = ref(null);
const loadingProbes = ref(false);
const loadingStrategies = ref(false);
const hasSearched = ref(false);
const isRestoringFromUrl = ref(false);
const dynamicFilters = ref([]); // danh sách filter của hướng tìm kiếm
const filterValues = ref({}); // giá trị người dùng chọn/nhập cho từng filter (key = filter.id)
const filterDropdownOptions = ref({}); // options cho dropdown (key = filter.id)
const loadingFilters = ref(false);

const getFilterLabel = (filter) => {
  const lang = locale.value || "vi";
  return (
    filter.name?.[lang] || filter.name?.vi || filter.name?.en || filter.code
  );
};

const getFilterPlaceholder = (filter) => {
  const lang = locale.value || "vi";
  return (
    filter.default_value_label?.[lang] || filter.default_value_label?.vi || ""
  );
};

const getFilterValue = (filter) => {
  const val = filterValues.value[filter.id];
  if (val !== undefined && val !== null && val !== "") return val;
  return getFilterPlaceholder(filter) || null;
};

const showSupportTypeRadio = computed(() => {
  if (!selectedStrategy.value) return false;
  const strategy = strategyOptions.value.find(
    (s) => s.value === selectedStrategy.value,
  );
  // Hiển thị khi strategy có cấu hình loại (tong_hop / chi_tiet / ca_hai)
  return !!strategy?.supportType;
});

const supportTypeOptions = computed(() => {
  const tongHop = { label: t("t_support_type_tong_hop"), value: "tong_hop" };
  const chiTiet = { label: t("t_support_type_chi_tiet"), value: "chi_tiet" };
  const strategy = strategyOptions.value.find(
    (s) => s.value === selectedStrategy.value,
  );
  const supportType = strategy?.supportType;
  if (supportType === "tong_hop") return [tongHop];
  if (supportType === "chi_tiet") return [chiTiet];
  // ca_hai (hoặc mặc định) hiển thị cả 2
  return [tongHop, chiTiet];
});

const effectiveSupportType = computed(() => {
  if (!selectedStrategy.value) return null;
  const strategy = strategyOptions.value.find(
    (s) => s.value === selectedStrategy.value,
  );
  if (!strategy) return null;
  if (strategy.supportType === "ca_hai") return selectedSupportType.value;
  return strategy.supportType;
});

const supportTypeToStype = (val) => {
  if (val === "tong_hop") return "sum";
  if (val === "chi_tiet") return "detail";
  return val;
};
const stypeToSupportType = (val) => {
  if (val === "sum") return "tong_hop";
  if (val === "detail") return "chi_tiet";
  return val;
};

// Chia dynamic filters thành các hàng (tối đa 4 filter trên 1 dòng)
const filterRows = computed(() => {
  const filters = dynamicFilters.value;
  const rows = [];
  for (let i = 0; i < filters.length; i += 4) {
    rows.push(filters.slice(i, i + 4));
  }
  return rows;
});

// Tính toán phần trăm độ rộng của từng filter trong hàng
const getFilterWidthPercent = (filter, row) => {
  if (!row || row.length === 0) return 100;
  
  // 1. Phân nhóm các filter đã cấu hình và chưa cấu hình độ rộng
  const configured = row.filter(f => {
    const w = Number(f.width);
    return w && w > 0;
  });
  
  const unconfiguredCount = row.length - configured.length;
  
  // 2. Tính tổng độ rộng các filter đã cấu hình
  const sumConfigured = configured.reduce((acc, f) => acc + Number(f.width), 0);
  
  // 3. Tính độ rộng còn lại cho các filter chưa cấu hình
  const remaining = 100 - sumConfigured;
  
  // 4. Xác định độ rộng của filter hiện tại
  const currentVal = Number(filter.width);
  const isConfigured = currentVal && currentVal > 0;
  
  if (isConfigured) {
    // Nếu tổng độ rộng cấu hình vượt quá 100%, fallback sang chia tỉ lệ theo trọng số
    if (sumConfigured >= 100) {
      const sum = row.reduce((acc, f) => acc + (Number(f.width) || 0), 0);
      return sum > 0 ? (currentVal / sum) * 100 : 100 / row.length;
    }
    return currentVal;
  } else {
    // Các filter chưa cấu hình độ rộng sẽ chia đều phần độ rộng còn lại
    if (remaining > 0 && unconfiguredCount > 0) {
      return remaining / unconfiguredCount;
    } else {
      // Fallback nếu không còn khoảng trống thừa (tổng cấu hình >= 100%)
      const sum = row.reduce((acc, f) => acc + (Number(f.width) || 100 / row.length), 0);
      const defaultWeight = 100 / row.length;
      return sum > 0 ? (defaultWeight / sum) * 100 : 100 / row.length;
    }
  }
};

const showDateRange = computed(() => effectiveSupportType.value === "tong_hop");

const showTimeRange = computed(() => effectiveSupportType.value === "chi_tiet");

const disableFutureDate = (date) => {
  return date.getTime() > Date.now();
};

const isSearchDisabled = computed(() => {
  if (!selectedProbe.value) return true;
  if (showDateRange.value && (!dateRange.value || dateRange.value.length !== 2))
    return true;
  if (
    showTimeRange.value &&
    (!datetimeRange.value || datetimeRange.value.length !== 2)
  )
    return true;
  return false;
});

const iframeUrl = ref("");
const loadingSearch = ref(false);
const showSearchPanel = ref(false);

// Tóm tắt thông số tìm kiếm hiển thị trên filter-section
const searchSummaryText = computed(() => {
  const parts = [];

  // Hướng
  if (selectedStrategy.value) {
    const strategy = strategyOptions.value.find(
      (s) => s.value === selectedStrategy.value,
    );
    if (strategy) {
      parts.push(`${t("t-dashboard-dynamic-search-result")} ${t("t-dashboard-dynamic-strategy-label")} <strong>${strategy.label}</strong>`);
    }
  }

  // Tỉnh/TP
  if (selectedProbe.value) {
    const probe = probeOptions.value.find((p) => p.value === selectedProbe.value);
    if (probe) {
      parts.push(`${t("t-dashboard-dynamic-province")} <strong>${probe.label}</strong>`);
    }
  }

  // Thời gian
  if (showDateRange.value && dateRange.value && dateRange.value.length === 2) {
    const formatDate = (d) => {
      const p = d.split("-");
      return `${p[2]}/${p[1]}/${p[0]}`;
    };
    parts.push(
      `${t("t-dashboard-dynamic-search-time-from")} <strong>${formatDate(dateRange.value[0])} - ${formatDate(dateRange.value[1])}</strong>`,
    );
  }
  if (showTimeRange.value && datetimeRange.value && datetimeRange.value.length === 2) {
    const start = datetimeRange.value[0];
    const end = datetimeRange.value[1];
    const startDate = start.substring(0, 10);
    const startTime = start.substring(11);
    const endTime = end.substring(11);
    const dateParts = startDate.split("-");
    const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
    parts.push(
      `${t("t-dashboard-dynamic-search-time-from")} <strong>${formattedDate}, ${startTime} - ${endTime}</strong>`,
    );
  }

  // Dynamic filters: chỉ hiện nếu giá trị khác giá trị mặc định
  for (const filter of dynamicFilters.value) {
    const val = filterValues.value[filter.id];
    if (val === undefined || val === null || val === "") continue;
    const defaultVal = getFilterPlaceholder(filter);
    if (val === defaultVal) continue;
    const label = getFilterLabel(filter);
    parts.push(`${label} <strong>${val}</strong>`);
  }

  return parts.join(", ");
});

const datePickerRef = ref(null);
const tabsContainerRef = ref(null);

const tabsScrollOffset = ref(0);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const updateScrollState = () => {
  const container = tabsContainerRef.value;
  if (!container) return;
  const tabsEl = container.querySelector(".search-hero__tabs");
  if (!tabsEl) return;
  const maxScroll = tabsEl.scrollWidth - container.clientWidth;
  canScrollLeft.value = tabsScrollOffset.value > 0;
  canScrollRight.value = maxScroll > 0 && tabsScrollOffset.value < maxScroll;
};

const scrollTabs = (direction) => {
  const container = tabsContainerRef.value;
  if (!container) return;
  const tabsEl = container.querySelector(".search-hero__tabs");
  if (!tabsEl) return;
  const maxScroll = Math.max(0, tabsEl.scrollWidth - container.clientWidth);
  const scrollAmount = 200;
  if (direction === "left") {
    tabsScrollOffset.value = Math.max(0, tabsScrollOffset.value - scrollAmount);
  } else {
    tabsScrollOffset.value = Math.min(
      maxScroll,
      tabsScrollOffset.value + scrollAmount,
    );
  }
  tabsEl.style.transform = `translateX(-${tabsScrollOffset.value}px)`;
  updateScrollState();
};

const openDatePicker = () => {
  nextTick(() => {
    datePickerRef.value?.focus();
  });
};

const dateRangeDisplay = computed(() => {
  if (!dateRange.value || dateRange.value.length !== 2) return null;
  const formatDate = (d) => {
    const parts = d.split("-"); // YYYY-MM-DD
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };
  return `${formatDate(dateRange.value[0])} - ${formatDate(
    dateRange.value[1],
  )}`;
});

const findMatchingSearchLink = async () => {
  if (!selectedStrategy.value) return null;
  mushroom.search_link.invalidCache();
  try {
    const response = await mushroom.search_link.getAllAsync({
      filters: Filter.eq("search_strategy_id", selectedStrategy.value).build(),
      fields: Project.include("id", "combination", "link").build(),
    });
    if (!response.result || response.result.length === 0) return null;

    for (const link of response.result) {
      if (isMatchingCombination(link.combination)) {
        return link.link?.dynamic_dashboard_id || null;
      }
    }
    return null;
  } catch (e) {
    console.error("Có lỗi khi tìm search_link: %o", e);
    return null;
  }
};

const isMatchingCombination = (combination) => {
  if (!combination || combination.length === 0) return true;

  const linkSupportType = combination.find((c) => c && c.support_type)
    ?.support_type;
  if (linkSupportType && linkSupportType !== effectiveSupportType.value) {
    return false;
  }

  for (const combo of combination) {
    if (!combo || !combo.search_filter_id) continue;
    const filter = dynamicFilters.value.find(
      (f) => String(f.id) === String(combo.search_filter_id),
    );
    if (!filter) return false;
    const val = filterValues.value[filter.id];
    const hasValue = val !== undefined && val !== null && val !== "";
    if (combo.type === "user_value") {
      // Filter yêu cầu người dùng nhập → phải có giá trị
      if (!hasValue) return false;
    } else {
      // Filter là "Mặc định" → người dùng KHÔNG được nhập giá trị (dùng default)
      if (hasValue) return false;
    }
  }
  return true;
};

const buildIframeUrl = (dynamicDashboardId) => {
  if (!selectedProbe.value) return "";
  const probe = probeOptions.value.find((p) => p.value === selectedProbe.value);
  if (!probe || !probe.url) return "";

  const tokenAccount = window.localStorage.getItem(
    "mushroom.tokens" + "[" + mushroom.$using() + "]",
  );
  const tokenFragment = MethodService.getTokenAccount(tokenAccount);
  const lang =
    localStorage.getItem("language") ||
    localStorage.getItem("language_default") ||
    "vi";

  let url = probe.url;
  let hashValue = "";
  let newUrl = "";

  if (url.includes("#")) {
    newUrl = url.substring(0, url.indexOf("#"));
    hashValue = url.substring(url.indexOf("#") + 1);
  } else {
    newUrl = url;
  }

  if (newUrl.includes("?")) {
    newUrl = newUrl + "&" + tokenFragment + "&lang=" + lang;
  } else {
    newUrl = newUrl + "?" + tokenFragment + "&lang=" + lang;
  }

  newUrl = newUrl + "&embed=true";

  if (effectiveSupportType.value) {
    newUrl += "&stype=" + supportTypeToStype(effectiveSupportType.value);
  }

  if (selectedProbe.value) {
    newUrl += "&pid_probe=" + encodeURIComponent(selectedProbe.value);
  }

  if (dynamicDashboardId) {
    newUrl += "&dynamic_dashboard_id=" + encodeURIComponent(dynamicDashboardId);
  }

  if (showDateRange.value && dateRange.value && dateRange.value.length === 2) {
    newUrl =
      newUrl +
      "&start_date=" +
      dateRange.value[0] +
      "&stop_date=" +
      dateRange.value[1];
  }

  if (
    showTimeRange.value &&
    datetimeRange.value &&
    datetimeRange.value.length === 2
  ) {
    const startDateObj = new Date(datetimeRange.value[0]);
    const stopDateObj = new Date(datetimeRange.value[1]);
    // start_time/stop_time giữ nguyên định dạng "YYYY-MM-DD HH:mm" (không encode)
    const startDt = datetimeRange.value[0];
    const stopDt = datetimeRange.value[1];
    // Lấy phần ngày (YYYY-MM-DD) theo giờ local, truyền y hệt như khi chọn tổng hợp
    newUrl =
      newUrl +
      "&start_date=" +
      MethodService.formatDateYYYYMMDD(startDateObj) +
      "&stop_date=" +
      MethodService.formatDateYYYYMMDD(stopDateObj) +
      "&start_time=" +
      encodeURIComponent(startDt) +
      "&stop_time=" +
      encodeURIComponent(stopDt);
  }

  // Thêm giá trị các dynamic filter vào URL
  for (const filter of dynamicFilters.value) {
    const val = getFilterValue(filter);
    if (val) {
      newUrl +=
        "&" + encodeURIComponent(filter.code) + "=" + encodeURIComponent(val);
    }
  }

  if (hashValue) {
    newUrl = newUrl + "#" + hashValue;
  }

  return newUrl;
};

const onSearch = async () => {
  loadingSearch.value = true;
  try {
    const dynamicDashboardId = await findMatchingSearchLink();
    if (!dynamicDashboardId) {
      Swal.fire({
        icon: "warning",
        title: t("t_caution"),
        html: `<b>${t("t-search-link-not-found")}</b><br/>${t(
          "t-search-link-contact-admin",
        )}`,
        confirmButtonText: "OK",
      });
      return;
    }
    iframeUrl.value = buildIframeUrl(dynamicDashboardId);
    hasSearched.value = true;
    pushQueryToUrl();
  } finally {
    loadingSearch.value = false;
  }
};

// Đẩy tham số tìm kiếm lên URL
const pushQueryToUrl = () => {
  const query = {};
  if (selectedProbe.value) query.probe = selectedProbe.value;
  if (selectedStrategy.value) query.strategy = selectedStrategy.value;
  if (effectiveSupportType.value)
    query.stype = supportTypeToStype(effectiveSupportType.value);
  if (dateRange.value && dateRange.value.length === 2) {
    query.start_date = dateRange.value[0];
    query.stop_date = dateRange.value[1];
  }
  if (datetimeRange.value && datetimeRange.value.length === 2) {
    query.start_time = datetimeRange.value[0];
    query.stop_time = datetimeRange.value[1];
    // Truyền kèm start_date/stop_date (phần ngày) giống như khi chọn tổng hợp
    query.start_date = MethodService.formatDateYYYYMMDD(datetimeRange.value[0]);
    query.stop_date = MethodService.formatDateYYYYMMDD(datetimeRange.value[1]);
  }
  // Dynamic filter values (có giá trị nhập hoặc default)
  for (const filter of dynamicFilters.value) {
    const val = getFilterValue(filter);
    if (val) {
      query[filter.code] = val;
    }
  }
  console.log("query", query);
  router.replace({ query }).catch(() => {});
};

// Khôi phục tham số từ URL
const restoreFromUrl = () => {
  const query = route.query;
  if (!query || Object.keys(query).length === 0) return false;
  isRestoringFromUrl.value = true;
  if (query.probe) selectedProbe.value = query.probe;
  if (query.strategy) selectedStrategy.value = query.strategy;
  if (query.stype) selectedSupportType.value = stypeToSupportType(query.stype);
  if (query.start_date && query.stop_date) {
    dateRange.value = [query.start_date, query.stop_date];
  }
  if (query.start_time && query.stop_time) {
    datetimeRange.value = [query.start_time, query.stop_time];
  }
  return !!(query.probe && query.strategy);
};

onMounted(async () => {
  await Promise.all([getProbes(), getStrategies()]);
  const hasUrlParams = restoreFromUrl();
  if (!hasUrlParams && strategyOptions.value.length > 0) {
    selectedStrategy.value = strategyOptions.value[0].value;
  }
  if (hasUrlParams) {
    try {
      // Chờ load filters cho strategy đã chọn
      await getFiltersByStrategy(selectedStrategy.value);
      // Khôi phục giá trị filter từ URL
      const query = route.query;
      for (const filter of dynamicFilters.value) {
        const key = filter.code;
        if (query[key] !== undefined) {
          filterValues.value[filter.id] = query[key];
        }
      }
      // Tự động tìm kiếm
      await onSearch();
    } finally {
      isRestoringFromUrl.value = false;
    }
  } else {
    isRestoringFromUrl.value = false;
  }
  nextTick(() => {
    updateScrollState();
    const container = tabsContainerRef.value;
    if (container) {
      new ResizeObserver(() => updateScrollState()).observe(container);
    }
  });
});

// Đưa màn tìm kiếm về trạng thái ban đầu (ẩn iframe, hiện lại form tìm kiếm)
const resetToInitial = () => {
  hasSearched.value = false;
  showSearchPanel.value = false;
  iframeUrl.value = "";
  selectedProbe.value = null;
  dateRange.value = null;
  datetimeRange.value = null;
  // Đặt lại hướng tìm kiếm mặc định
  if (strategyOptions.value.length > 0) {
    selectedStrategy.value = strategyOptions.value[0].value;
  }
};

// Khi người dùng bấm lại menu "Tìm kiếm" thì hiển thị lại form tìm kiếm
watch(
  () => route.query,
  (newQuery) => {
    if (isRestoringFromUrl.value) return;
    const isEmptyQuery = !newQuery || Object.keys(newQuery).length === 0;
    if (isEmptyQuery && hasSearched.value) {
      resetToInitial();
    }
  },
);

watch(selectedStrategy, (newVal) => {
  if (isRestoringFromUrl.value) return;
  // Đặt loại mặc định theo cấu hình strategy: chỉ chi_tiet -> chi_tiet, còn lại -> tong_hop
  const strategy = strategyOptions.value.find((s) => s.value === newVal);
  selectedSupportType.value =
    strategy?.supportType === "chi_tiet" ? "chi_tiet" : "tong_hop";
  dateRange.value = null;
  datetimeRange.value = null;
  iframeUrl.value = "";
  getFiltersByStrategy(newVal);
});

const getProbes = async () => {
  loadingProbes.value = true;
  try {
    const response = await mushroom.probe.get_probesAsync({
      body: { filters: { type: localStorage.getItem("type_login") } },
    });
    if (response.result && response.result.length > 0) {
      probeOptions.value = response.result.map((item) => {
        const cols = item.columns || {};
        return {
          value: item.id,
          label: cols.name || item.name || item.id,
          url: item.url || "",
        };
      });
    }
  } catch (e) {
    console.error("Có lỗi khi lấy danh sách probe: %o", e);
  } finally {
    loadingProbes.value = false;
  }
};

const getStrategies = async () => {
  loadingStrategies.value = true;
  try {
    mushroom.search_strategy.invalidCache();
    const response = await mushroom.search_strategy.getAllAsync({
      fields: Project.include(
        "id",
        "name",
        "index",
        "support_type",
        "tooltip",
        "icon",
      ).build(),
      sort: Sort.ascending("index").thenByAscending("name.vi").build(),
    });
    if (response.result && response.result.length > 0) {
      strategyOptions.value = response.result.map((item) => ({
        value: item.id,
        label: item.name?.vi || item.name?.en || item.id,
        supportType: item.support_type || "",
        icon: item.icon || "",
        tooltip: item.tooltip || "",
      }));
    }
  } catch (e) {
    console.error("Có lỗi khi lấy danh sách hướng tìm kiếm: %o", e);
  } finally {
    loadingStrategies.value = false;
  }
};

// Lấy danh sách filter theo hướng tìm kiếm đã chọn
const getFiltersByStrategy = async (strategyId) => {
  if (!strategyId) {
    dynamicFilters.value = [];
    filterValues.value = {};
    filterDropdownOptions.value = {};
    return;
  }
  loadingFilters.value = true;
  try {
    mushroom.search_filter.invalidCache();
    const response = await mushroom.search_filter.getAllAsync({
      filters: Filter.eq("search_strategy_id", strategyId).build(),
      fields: Project.include(
        "id",
        "code",
        "name",
        "input_type",
        "default_value_label",
        "source",
        "index",
        "width",
        "tooltip",
      ).build(),
      sort: "index",
    });
    if (response.result && response.result.length > 0) {
      dynamicFilters.value = response.result;
      // Khởi tạo giá trị mặc định
      const values = {};
      for (const filter of response.result) {
        values[filter.id] = null;
      }
      filterValues.value = values;
      // Lấy options cho các filter dropdown
      await loadDropdownOptions(response.result);
    } else {
      dynamicFilters.value = [];
      filterValues.value = {};
      filterDropdownOptions.value = {};
    }
  } catch (e) {
    console.error("Có lỗi khi lấy danh sách filter: %o", e);
    dynamicFilters.value = [];
  } finally {
    loadingFilters.value = false;
  }
};

// Lấy options cho tất cả filter dạng dropdown
const loadDropdownOptions = async (filters) => {
  const dropdownFilters = filters.filter((f) => f.input_type === "dropdown");
  const optionsMap = {};
  await Promise.all(
    dropdownFilters.map(async (filter) => {
      // Nếu filter chưa cấu hình nguồn dữ liệu thì bỏ qua
      if (!filter.source || !filter.source.from) {
        optionsMap[filter.id] = [];
        return;
      }
      try {
        const response =
          await mushroom.search_filter.views.get_data_sourceAsync({
            id: filter.id,
          });
        if (response.result) {
          const data = Array.isArray(response.result)
            ? response.result
            : [response.result];
          // Chuẩn hóa: nếu item là string thì chuyển thành {value, label}
          optionsMap[filter.id] = data.map((item) => {
            if (typeof item === "string") {
              return { value: item, label: item };
            }
            return item;
          });
        } else {
          optionsMap[filter.id] = [];
        }
      } catch (e) {
        console.error(
          "Có lỗi khi lấy data source cho filter %s: %o",
          filter.code,
          e,
        );
        optionsMap[filter.id] = [];
      }
    }),
  );
  filterDropdownOptions.value = optionsMap;
};

watch(selectedSupportType, () => {
  if (isRestoringFromUrl.value) return;
  dateRange.value = null;
  datetimeRange.value = null;
  iframeUrl.value = "";
});
</script>

<template>
  <Layout>
    <div class="search-dashboard-dynamic">
      <div class="dashboard-dynamic-wrapper">
        <!-- Nút toggle tìm kiếm (chỉ hiện khi đã search) -->
        <div v-if="hasSearched" class="filter-section">
          <div class="filter-left">
            <span class="filter-summary-text" v-html="searchSummaryText"></span>
          </div>
          <div class="filter-right">
            <button
              class="btn btn-secondary btn-border btn-toggle-search"
              :class="{ active: showSearchPanel }"
              @click="showSearchPanel = !showSearchPanel"
            >
              <i class="ri-search-line"></i>
            </button>
          </div>
        </div>

        <!-- Phần iframe -->
        <div class="iframe-section">
          <!-- Search panel overlay -->
          <transition name="search-panel-fade">
            <div
              v-if="!hasSearched || showSearchPanel"
              class="search-panel-overlay"
              :class="{ 'search-panel-overlay--hero': !hasSearched }"
            >
              <div
                class="search-panel-overlay__backdrop"
                @click="hasSearched ? (showSearchPanel = false) : null"
              ></div>
              <div class="search-hero__content">
          <!-- Title -->
          <h2 class="search-hero__title">
            {{ $t("t-dashboard-dynamic-search-title") }}
          </h2>

          <!-- Search type tabs (hướng tìm kiếm) -->
          <div
            v-if="strategyOptions.length > 0"
            class="search-hero__tabs-wrapper"
          >
            <div
              v-if="canScrollLeft"
              class="search-hero__scroll-btn search-hero__scroll-btn--left"
              @click="scrollTabs('left')"
            >
              <i class="ri-arrow-left-s-line"></i>
            </div>
            <div class="search-hero__tabs-scroll" ref="tabsContainerRef">
              <div class="search-hero__tabs">
                <template v-for="item in strategyOptions" :key="item.value">
                  <el-tooltip
                    v-if="item.tooltip"
                    :content="item.tooltip"
                    placement="top"
                  >
                    <div
                      class="search-hero__tab"
                      :class="{
                        'search-hero__tab--active':
                          selectedStrategy === item.value,
                      }"
                      @click="selectedStrategy = item.value"
                    >
                      <img
                        v-if="MethodService.isIconId(item.icon)"
                        :src="MethodService.thumbIcon(item.icon, 20)"
                        alt="icon"
                        class="search-hero__tab-icon-img"
                      />
                      <i
                        v-else
                        :class="item.icon || 'ri-compass-3-line'"
                        class="search-hero__tab-icon"
                      ></i>
                      <span class="search-hero__tab-label">{{
                        item.label
                      }}</span>
                    </div>
                  </el-tooltip>
                  <div
                    v-else
                    class="search-hero__tab"
                    :class="{
                      'search-hero__tab--active':
                        selectedStrategy === item.value,
                    }"
                    @click="selectedStrategy = item.value"
                  >
                    <img
                      v-if="MethodService.isIconId(item.icon)"
                      :src="MethodService.thumbIcon(item.icon, 20)"
                      alt="icon"
                      class="search-hero__tab-icon-img"
                    />
                    <i
                      v-else
                      :class="item.icon || 'ri-compass-3-line'"
                      class="search-hero__tab-icon"
                    ></i>
                    <span class="search-hero__tab-label">{{ item.label }}</span>
                  </div>
                </template>
              </div>
            </div>
            <div
              v-if="canScrollRight"
              class="search-hero__scroll-btn search-hero__scroll-btn--right"
              @click="scrollTabs('right')"
            >
              <i class="ri-arrow-right-s-line"></i>
            </div>
          </div>

          <!-- Search form panel -->
          <div class="search-hero__panel">
            <!-- Row 1: Chọn trạm, Segmented, Chọn ngày -->
            <div class="search-hero__row">
              <div class="search-hero__field search-hero__field--probe">
                <label class="search-hero__field-label">{{
                  $t("t-dashboard-dynamic-province")
                }}</label>
                <el-select
                  v-model="selectedProbe"
                  filterable
                  :placeholder="$t('t-place-dashboard-dynamic-probe')"
                  :loading="loadingProbes"
                  :no-match-text="$t('t-match-data')"
                  :no-data-text="$t('t-no-data')"
                  size="large"
                  style="width: 150px"
                >
                  <el-option
                    v-for="item in probeOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>
              <div
                v-if="showSupportTypeRadio"
                class="search-hero__field search-hero__field--segmented"
              >
                <label class="search-hero__field-label">{{
                  $t("t-dashboard-dynamic-support-type")
                }}</label>
                <el-segmented
                  v-model="selectedSupportType"
                  :options="supportTypeOptions"
                  size="default"
                  :style="{ width: supportTypeOptions.length > 1 ? '200px' : '100px' }"
                />
              </div>
              <div
                v-if="selectedStrategy && showDateRange"
                class="search-hero__field"
              >
                <label class="search-hero__field-label">{{
                  $t("t-dashboard-dynamic-time")
                }}</label>
                <div
                  class="custom-date-trigger"
                  style="height: 40px; width: 100%"
                  @click="openDatePicker"
                >
                  <i class="ri-calendar-line"></i>
                  <span v-if="dateRangeDisplay">{{ dateRangeDisplay }}</span>
                  <span v-else class="trigger-placeholder"
                    >{{ $t("t-start-time") }} - {{ $t("t-end-time") }}</span
                  >
                </div>
                <div class="hidden-picker-wrap">
                  <el-date-picker
                    ref="datePickerRef"
                    v-model="dateRange"
                    type="daterange"
                    :start-placeholder="$t('t-start-time')"
                    :end-placeholder="$t('t-end-time')"
                    format="DD/MM/YYYY"
                    value-format="YYYY-MM-DD"
                    :disabled-date="disableFutureDate"
                    popper-class="center-arrow-date-popper"
                  />
                </div>
              </div>
              <div
                v-if="selectedStrategy && showTimeRange"
                class="search-hero__field"
              >
                <label class="search-hero__field-label">{{
                  $t("t-dashboard-dynamic-time")
                }}</label>
                <StepDateTimePicker
                  v-model="datetimeRange"
                  :disabled-date="disableFutureDate"
                  :start-placeholder="$t('t-start-time')"
                  :end-placeholder="$t('t-end-time')"
                  trigger-height="40px"
                />
              </div>
            </div>

            <!-- Row 2+: Dynamic filters (chia hàng theo số lượng) -->
            <template v-if="selectedStrategy && dynamicFilters.length > 0">
              <div
                v-for="(row, rowIndex) in filterRows"
                :key="'filter-row-' + rowIndex"
                class="search-hero__row search-hero__row--filters"
              >
                <template v-for="filter in row" :key="filter.id">
                  <div
                    class="search-hero__filter-item"
                    :style="{ flex: `${getFilterWidthPercent(filter, row)} ${getFilterWidthPercent(filter, row)} 0%` }"
                  >
                    <label class="search-hero__filter-label">
                      <el-tooltip
                        v-if="filter.tooltip"
                        :content="filter.tooltip"
                        placement="top"
                      >
                        <span>{{ getFilterLabel(filter) }}</span>
                      </el-tooltip>
                      <template v-else>
                        {{ getFilterLabel(filter) }}
                      </template>
                    </label>
                    <el-select
                      v-if="filter.input_type === 'dropdown'"
                      v-model="filterValues[filter.id]"
                      filterable
                      clearable
                      :placeholder="
                        getFilterPlaceholder(filter) || $t('t-select')
                      "
                      :no-match-text="$t('t-match-data')"
                      :no-data-text="$t('t-no-data')"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="opt in filterDropdownOptions[filter.id] || []"
                        :key="opt.value"
                        :label="opt.label || opt.value"
                        :value="opt.value"
                      />
                    </el-select>
                    <el-input
                      v-else
                      v-model="filterValues[filter.id]"
                      :placeholder="getFilterPlaceholder(filter)"
                      clearable
                      style="width: 100%"
                    />
                  </div>
                </template>
              </div>
            </template>

            <!-- Search button -->
            <div class="search-hero__actions">
              <button
                class="btn btn-warning btn-hero-search"
                :disabled="isSearchDisabled"
                @click="onSearch(); showSearchPanel = false;"
              >
                <i class="ri-search-line"></i>
                {{ $t("t-search") }}
              </button>
            </div>
          </div>
          <!-- /search-hero__panel -->
              </div>
              <!-- /search-hero__content -->
            </div>
            <!-- /search-panel-overlay -->
          </transition>

          <!-- Iframe / Placeholder -->
          <iframe
            v-if="iframeUrl"
            :src="iframeUrl"
            class="iframe-content"
            frameborder="0"
            allowfullscreen
          ></iframe>
          <div
            v-else-if="hasSearched"
            class="iframe-placeholder"
            :class="{ 'iframe-placeholder--step2': selectedStrategy }"
          >
            <div class="placeholder-content">
              <template v-if="selectedStrategy">
                <div class="es-icon-wrap">
                  <div class="es-ring es-ring--blue"></div>
                  <div class="es-icon-inner es-icon-inner--blue">
                    <i class="ri-search-line"></i>
                  </div>
                </div>
                <h4 class="placeholder-title">
                  {{ $t("t-dashboard-dynamic-ready-title") }}
                </h4>
                <p class="placeholder-desc">
                  {{ $t("t-dashboard-dynamic-ready-desc") }}
                </p>
              </template>
              <template v-else>
                <div class="es-icon-wrap">
                  <div class="es-ring"></div>
                  <div class="es-icon-inner">
                    <i class="ri-base-station-line"></i>
                  </div>
                </div>
                <h4 class="placeholder-title">
                  {{ $t("t-dashboard-dynamic-select-probe") }}
                </h4>
                <p class="placeholder-desc">
                  {{ $t("t-dashboard-dynamic-placeholder-desc") }}
                </p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped lang="scss">
@import "./dashboard-dynamic-view.scss";
</style>

<style lang="scss">
.center-arrow-date-popper {
  .el-popper__arrow {
    left: 50% !important;
    transform: translateX(-50%) !important;
  }
}
</style>
